<template>
  <VaCard class="flex-nowrap">
    <VaCardTitle class="flex justify-between">
      <h3 class="mb-3 font-bold text-lg" style="text-transform: none">Deshabilitar tripulante</h3>
    </VaCardTitle>
    <VaCardContent class="overflow-x-auto">
      <div class="flex flex-col gap-2">
        <div class="body-content">
          <div class="scrollable-data-list">
            <VaDataTable
              v-if="showSearch"
              class="region-revenue-table"
              :columns="[
                { key: 'nombre', label: 'Nombre' },
                { key: 'rut', label: 'RUT', align: 'left' },
                { key: 'cargo', label: 'Cargo', align: 'left' },
                { key: 'activeViajes', label: 'Viajes Activos', align: 'center' },
                { key: 'embarcaciones', label: 'Embarcaciones' },
              ]"
              :items="showList"
              selectable
              select-mode="multiple"
              selected-color="primary"
              @selectionChange="selectedItemsEmitted = $event.currentSelectedItems"
            >
              <template #cell(nombre)="{ rowData }">
                {{ rowData.nombre_completo }}
              </template>
              <template #cell(rut)="{ rowData }">
                {{ rowData.rut }}
              </template>
              <template #cell(cargo)="{ rowData }">
                {{
                  rowData.activeViajes
                    .reduce((acc, viaje) => {
                      const patron = viaje.patronData.id == rowData.id
                      if (patron && !acc.includes('Patrón')) {
                        acc.push('Patrón')
                      } else if (!patron && !acc.includes('Tripulante')) {
                        acc.push('Tripulante')
                      }
                      return acc
                    }, [] as string[])
                    .sort((a, b) => a.localeCompare(b))
                    .join(', ')
                }}
              </template>
              <template #cell(activeViajes)="{ rowData }">
                {{ rowData.activeViajes.length }}
              </template>
              <template #cell(embarcaciones)="{ rowData }">
                <div class="flex flex-col gap-1">
                  <div
                    v-for="viaje in rowData.activeViajes.reduce((uniqueEmb, viaje) => {
                      if (!uniqueEmb.some((v) => v.embarcacionData.id === viaje.embarcacionData.id)) {
                        uniqueEmb.push(viaje)
                      }
                      return uniqueEmb
                    }, [] as ViajeEmbarcacion[])"
                    :key="viaje.id"
                    class="px-2 py-1 rounded"
                    :style="{ backgroundColor: getColor('primary-lighten-4'), color: 'white' }"
                  >
                    {{ '(' + viaje.embarcacionData.id_lancha + ') ' + viaje.embarcacionData.nombre_embarcacion }}
                  </div>
                </div>
              </template>
            </VaDataTable>
          </div>
          <VaButton
            v-if="
              showList.length > 0 &&
              (userRole!.role == UserRoles.ADMIN ||
                userRole!.role == UserRoles.SUPERVISOR ||
                userRole!.role == UserRoles.OPERADOR)
            "
            :disabled="selectedItemsEmitted.length == 0"
            class="mt-2"
            preset="danger"
            color="danger"
            @click="disableTripulantesSeleccionados"
          >
            Deshabilitar permisos de tripulantes seleccionados
          </VaButton>
        </div>
      </div>
    </VaCardContent>
  </VaCard>
</template>

<script setup lang="ts">
import { onMounted, ref, Ref, watch } from 'vue'
import { useColors, useModal, useToast, VaButton } from 'vuestic-ui'
import { IRequest, RequestObjectType, RequestStatus } from '../../../services/api-services/RequestsServices'
import dataStorageManager, { BucketDefaultIDs } from '../../../managers/data/DataStorageManager'
import { IArmador, OperatedArmador } from '../../../services/api-services/ArmadoresServices'
import { IUser, UserRoles } from '../../../services/api-services/UsersServices'
import { rutify, normalizeRut, rutMatches } from '../../../services/apiUtils'
import { ITripulante, OperatedTripulante } from '../../../services/api-services/TripulantesServices'
import identityManager, { LoggedInUser } from '../../../managers/identity/IdentityManager'
import {
  defaultIIncidencia,
  IIncidencia,
  IncidenceCode,
  IncidenceType,
} from '../../../services/api-services/BitacoraServices'
import { IViaje } from '../../../services/api-services/ViajesServices'
import roleCapacitiesManager, { UserRole } from '../../../managers/identity/RoleCapacitiesManager'
import { IEmbarcacion } from '../../../services/api-services/EmbarcacionesServices'

interface ViajeEmbarcacion extends IViaje {
  embarcacionData: IEmbarcacion
  tripulanteRequest: IRequest
  patronData: ITripulante
}

interface TripulanteViajes extends OperatedTripulante {
  activeViajes: ViajeEmbarcacion[]
}

const { init: notify } = useToast()
const { confirm } = useModal()

const { getColor } = useColors()

const userRole: Ref<UserRole | null> = roleCapacitiesManager.getRefUserRole()

const armadoresList: Ref<Array<IArmador>> = ref([])

const usersList: Ref<Array<IUser>> = ref([])
const tripulantesList: Ref<Array<ITripulante>> = ref([])
const fullTripulantesList: Ref<Array<TripulanteViajes>> = ref([])

const embarcacionesList: Ref<Array<IEmbarcacion>> = ref([])

const requestsList: Ref<IRequest[]> = ref([])
const armadoresRequests: Ref<IRequest[]> = ref([])
const changesRequests: Ref<IRequest[]> = ref([])
const asociationsRequests: Ref<IRequest[]> = ref([])

const preacreditacionRequests: Ref<IRequest[]> = ref([])
const posicionamientoRequests: Ref<IRequest[]> = ref([])
const patronesRequests: Ref<IRequest[]> = ref([])
const viajesList: Ref<IViaje[]> = ref([])

const selectedPeriod = ref('RUT') as Ref<'RUT' | 'Nombre'>
const periods = ['RUT', 'Nombre'].map((period) => ({ label: period, value: period }))
const searchQuery = ref('')
const showSearch = ref(true)
const selectedItemsEmitted: Ref<Array<TripulanteViajes>> = ref([])

const dataList: Ref<Array<TripulanteViajes>> = ref([])
const showList: Ref<Array<TripulanteViajes>> = ref([])

onMounted(() => {
  dataStorageManager.subscribeBucket(BucketDefaultIDs.EMBARCACIONES, (embarcaciones: Array<IEmbarcacion>) => {
    embarcacionesList.value = embarcaciones
    loadFullTripulantesList()
  })
  dataStorageManager.subscribeBucket(BucketDefaultIDs.ARMADORES, (armadores: Array<IArmador>) => {
    armadoresList.value = armadores
    loadFullTripulantesList()
  })
  dataStorageManager.subscribeBucket(BucketDefaultIDs.USERS, (usuarios: Array<IUser>) => {
    usersList.value = usuarios
    loadFullTripulantesList()
  })
  dataStorageManager.subscribeBucket<IViaje>(BucketDefaultIDs.VIAJES, (viajes) => {
    onViajesLoad(viajes)
  })
  dataStorageManager.subscribeBucket(BucketDefaultIDs.TRIPULANTES, (tripulantes: Array<ITripulante>) => {
    tripulantesList.value = tripulantes
    loadFullTripulantesList()
  })

  dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, onRequestsLoad)
})

watch(fullTripulantesList, () => {
  dataList.value = fullTripulantesList.value
})

watch(dataList, () => {
  showList.value = dataList.value
})

watch(searchQuery, () => {
  if (searchQuery.value.trim() === '') {
    showList.value = dataList.value
  } else {
    const query = searchQuery.value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    if (selectedPeriod.value === 'RUT') {
      showList.value = dataList.value.filter((item) => rutMatches(item.rut, searchQuery.value))
    } else if (selectedPeriod.value === 'Nombre') {
      showList.value = dataList.value.filter((item) =>
        item.nombre_completo.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().includes(query),
      )
    }
  }
})

watch(selectedPeriod, () => {
  searchQuery.value = ''
  showList.value = dataList.value
})

function onRequestsLoad(requests: Array<IRequest>) {
  requestsList.value = requests.filter(
    (r) => r.tipo_objeto === RequestObjectType.Tripulante && r.tipo_objeto_2 === null,
  )
  changesRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Modificacion)
  asociationsRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Asociacion_tripulante)
  preacreditacionRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Preacreditacion)
  posicionamientoRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PosicionamientoViaje)
  patronesRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PatronViaje)
  loadFullTripulantesList()
}

function loadFullTripulantesList() {
  fullTripulantesList.value = tripulantesList.value
    .map((item) => {
      let request: IRequest | undefined = requestsList.value
        .filter((req) => req.tipo_objeto === RequestObjectType.Tripulante)
        .filter((req) => req.id_objeto.toString() === item.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
      const asociationsData = asociationsRequests.value
        .filter((req) => req.id_objeto.toString() === item.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())

      const armadorData = armadoresList.value.find((a) => rutify(a.rut_representante) === rutify(item.rut))
      const armadorRequest = armadoresRequests.value
        .filter((req) => req.id_objeto.toString() === armadorData?.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request

      const preacArmadorPatron = preacreditacionRequests.value.filter(
        (req) =>
          req.tipo_objeto_2 === RequestObjectType.Tripulante &&
          req.id_objeto_2 &&
          req.id_objeto_2.toString() === item.id.toString(),
      )
      const activeViajes: Array<ViajeEmbarcacion> = viajesList.value
        .filter((v) => {
          const preArmPtr = preacArmadorPatron
            .filter((p) => p.id_objeto.toString() === v.id.toString())
            .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
          return (
            new Date(v.fecha_viaje).getTime() >= new Date().getTime() - 1000 * 60 * 60 * 10 &&
            new Date(v.fecha_viaje).getTime() <= new Date().getTime() + 1000 * 60 * 60 * 10 &&
            preArmPtr &&
            preArmPtr.estado == RequestStatus.APPROVED
          )
        })
        .map((viaje) => {
          const tripulanteRequest = preacArmadorPatron
            .filter((req) => req.id_objeto.toString() === viaje.id.toString())
            .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
          const requestPatron = patronesRequests.value
            .filter((req) => req.id_objeto.toString() === viaje.id.toString())
            .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
          const embarcacionData = embarcacionesList.value.find(
            (e) => e.id.toString() === viaje.embarcacion_id.toString(),
          )
          if (requestPatron && tripulanteRequest) {
            const patronData = tripulantesList.value.find(
              (p) => p.id.toString() === requestPatron.id_objeto_2!.toString(),
            )!
            return {
              ...viaje,
              embarcacionData: embarcacionData,
              tripulanteRequest: tripulanteRequest,
              patronData: patronData,
            }
          } else {
            return undefined
          }
        })
        .filter((v) => v !== undefined) as Array<ViajeEmbarcacion>

      const userData = usersList.value.find((u) => rutify(u.rut) === rutify(item.rut))
      if (armadorData && userData) {
        item.imagen_perfil = userData.id_gestor_fotografia
        item.carnet_adjunto = userData.id_gestor_ci
      }

      let status = request ? request.estado : RequestStatus.INCOMPLETE
      const imagenPerfilValid = item.imagen_perfil !== 0
      const matriculaValid = item.id_matricula !== 0
      const carnetValid = item.carnet_adjunto !== 0

      const validDocs = matriculaValid && carnetValid && imagenPerfilValid
      if (!validDocs) {
        status = RequestStatus.INCOMPLETE
        request = undefined
      }
      return {
        ...item,
        requestData: armadorRequest ? armadorRequest : request,
        changesData: [],
        asociationsData: asociationsData,
        armadorData: armadorData,
        userData: userData,
        activeViajes: activeViajes,
      }
    })
    .filter(
      (e) =>
        e.activeViajes.length > 0 &&
        e.requestData &&
        (e.requestData.estado === RequestStatus.APPROVED || e.requestData.estado === RequestStatus.REJECTED),
    )
    .sort((a, b) => a.nombre_completo.localeCompare(b.nombre_completo))
}

function onViajesLoad(viajes: Array<IViaje>) {
  viajesList.value = viajes
  loadFullTripulantesList()
}

const exportAsCSV = () => {}

async function disableTripulantesSeleccionados() {
  const confirmed = await confirm({
    title: 'Desactivar tripulante',
    message: `¿Estás seguro de desactivar los tripulantes seleccionados?`,
    okText: 'Sí, deshabilitar',
    cancelText: 'Cancelar',
  })
  if (confirmed) {
    const tripulantesPromises = selectedItemsEmitted.value.map(async (tripulante) => {
      await onDesacreditar(tripulante)
    })
    await Promise.all(tripulantesPromises)
    notify({
      message: `Tripulantes deshabilitados correctamente.`,
      color: 'success',
    })
  }
}

async function onDesacreditar(tripulante: TripulanteViajes) {
  const currentUser: LoggedInUser = identityManager.getUser()!

  const viajesPromises = tripulante.activeViajes.map(async (viajeAModificar) => {
    const revokedRequest: IRequest = {
      ...viajeAModificar.tripulanteRequest,
      revisor: currentUser.id_usuario,
      fecha_respuesta: new Date().toISOString(),
      estado: RequestStatus.REJECTED,
      fecha_caducidad: new Date().toISOString(),
      razon_rechazo: `Preacreditación revocada por deshabilitación de armador.`,
    }
    await dataStorageManager.performOperation(BucketDefaultIDs.SOLICITUDES, 'updateRequest', revokedRequest)
    const bitacoraData: IIncidencia = { ...defaultIIncidencia }
    bitacoraData.tipo_incidencia_id = IncidenceType.PREACREDITACION
    bitacoraData.codigo_incidencia = IncidenceCode.DESACREDITACION
    bitacoraData.descripcion = `Preacreditación desacreditada para viaje ID ${viajeAModificar.id} y tripulante ID ${viajeAModificar.tripulanteRequest.id_objeto_2}.`
    bitacoraData.id_objeto = viajeAModificar.id
    await dataStorageManager.performOperation(BucketDefaultIDs.BITACORA, 'insertIncidencia', bitacoraData)
  })

  await Promise.all(viajesPromises)
}
</script>

<style lang="scss" scoped>
.region-revenue-table {
  ::v-deep(tbody) {
    tr {
      border-top: 1px solid var(--va-background-border);
    }
  }
}

.pers-content-container {
  max-height: 636px;
  height: 100%;
}

.scrollable-data-list {
  max-height: 520px;
  overflow-y: auto;
}

.region-revenue-table {
  min-width: 360px;
}

.body-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
}

.gapped-div {
  gap: 10px;
}

.padded-button {
  padding: 0 10px;
}
</style>
